import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth.jsx'
import { supabase } from '../lib/supabase.js'
import { uploadMedia } from '../lib/assetMedia.js'
import { MediaGallery, MediaPicker } from './MediaAttachments.jsx'

export default function AssetNotes({ symbol, children }) {
  const owner = useAuth()?.user?.id
  return <AssetNotesBody key={`${owner}:${symbol}`} owner={owner} symbol={symbol}>{children}</AssetNotesBody>
}

function AssetNotesBody({ owner, symbol, children }) {
  // The parent keys this component by asset; pending work never clears another draft.
  const [text, setText] = useState(''), [files, setFiles] = useState([]), [uploaded, setUploaded] = useState(null)
  const [notes, setNotes] = useState([]), [error, setError] = useState(''), [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [recording, setRecording] = useState(false)
  const [reload, setReload] = useState(0)
  const [draftId, setDraftId] = useState(() => crypto.randomUUID())
  useEffect(() => {
    let active = true
    setLoading(true); setNotes([])
    if (!owner || !symbol) { setLoading(false); return }
    supabase.from('asset_notes').select('id, body, attachments, created_at').eq('user_id', owner).eq('symbol', symbol).order('created_at', { ascending: false }).limit(100).then(({ data, error }) => {
      if (!active) return
      if (error) setError('Notes could not be loaded. Retry to check saved notes.')
      else setNotes(data || [])
      setLoading(false)
    }).catch(() => { if (active) { setError('Notes could not be loaded. Retry to check saved notes.'); setLoading(false) } })
    return () => { active = false }
  }, [owner, symbol, reload])
  async function save() {
    if (busy || recording || !owner || (!text.trim() && !files.length)) return
    setBusy(true); setError('')
    try {
      const attachments = uploaded || await uploadMedia(supabase, owner, files)
      setUploaded(attachments)
      const { error } = await supabase.from('asset_notes').insert({ id: draftId, user_id: owner, symbol, body: text.trim(), attachments })
      // A retry after a lost response must not duplicate a note.
      if (error && error.code !== '23505') throw error
      setText(''); setFiles([]); setUploaded(null); setDraftId(crypto.randomUUID()); setReload(value => value + 1)
    } catch (failure) { setError(failure.message || 'Note could not be saved. Your draft is retained.') }
    finally { setBusy(false) }
  }
  return <div className="space-y-4">
    <div className="space-y-2">
      <label className="block text-xs text-inkMuted">Note for {symbol}<textarea aria-label={`Note for ${symbol}`} value={text} maxLength={10000} disabled={busy || Boolean(uploaded)} onChange={event => setText(event.target.value)} rows={3} placeholder="Thoughts, thesis, or reminders…" className="mt-1 w-full rounded border border-border bg-canvas p-2 text-xs text-ink"/></label>
      <MediaPicker files={files} onChange={setFiles} disabled={busy || Boolean(uploaded)} onRecordingChange={setRecording}/>
      <button type="button" onClick={save} disabled={busy || recording || !owner || (!text.trim() && !files.length)} className="rounded bg-ink px-3 py-1.5 text-xs text-canvas disabled:opacity-40">{busy ? 'Saving…' : uploaded ? 'Retry saving note' : 'Add Note'}</button>
      {error && <p role="alert" className="text-xs text-red-500">{error} <button type="button" onClick={() => { setError(''); setReload(value => value + 1) }} className="underline">Reload notes</button></p>}
    </div>
    {loading && <p role="status" className="text-xs text-inkMuted">Loading notes…</p>}
    {!loading && !notes.length && !error && <p className="text-xs text-inkMuted">No asset notes yet.</p>}
    {notes.map(note => <article key={note.id} className="rounded border border-border p-3"><p className="text-[10px] text-inkMuted">{new Date(note.created_at).toLocaleString()}</p><p className="whitespace-pre-wrap break-words text-xs text-ink">{note.body}</p><MediaGallery attachments={note.attachments}/></article>)}
    {notes.length === 100 && <p className="text-xs text-inkMuted">Showing the latest 100 notes for this asset.</p>}
    {children}
  </div>
}
